import {
  CarbonIntensity,
  CarbonIntensities
} from '@/models/carbon/CarbonIntensity.js'
import {
  ImpactIntensity,
  ImpactIntensities
} from '@/models/carbon/ImpactIntensity.js'
import WASTES_FACTORS from '@/../data/factors/carbon/wastesFactors.json'
import WASTES_LCA_FACTORS from '@/../data/factors/lca/wastesFactors.json'
import Model from '@/models/carbon/Model.js'
import Modules from '@/models/Modules.js'
import { EmissionFactor, ImpactFactor } from '@/models/carbon/Factor.js'
import { floatConverter, tagsConverter } from '@/utils/parser.js'

const DEFAULT_GROUP = 'packaging'
const DEFAULT_TYPE = 'wood'
const DEFAULT_VALORIZATION = 'incineration'

export default class Waste extends Model {
  constructor ({
    id = null,
    group = DEFAULT_GROUP,
    type = DEFAULT_TYPE,
    valorization = DEFAULT_VALORIZATION,
    amount = 0,
    tags = [],
    source = null
  } = {}) {
    super({ tags, source })
    this.id = id
    this.group = group
    this.type = type
    this.valorization = valorization
    this.amount = this.setAmount(amount)
    this.intensity = new CarbonIntensity()
    this.impact = new ImpactIntensity()
  }

  descriptor () {
    return this.group + '_' + this.type + '_' + this.valorization
  }

  isSimilarTo (other) {
    return (
      this === other ||
      this.id === other.id ||
      this.descriptor === other.descriptor
    )
  }

  get score () {
    let score = 1
    if (!this.group || !this.type || !this.valorization || !this.amount) {
      score = 2
    }
    return score
  }

  clean () {
    // coerce to float amount
    // (input form might set this as string)
    this.amount = parseFloat(this.amount)
  }

  toString (sep = '\t') {
    let values = [
      this.group,
      this.type,
      this.valorization,
      this.amount,
      this.tags.map((val) => val.name).join(';'),
      Math.round(this.intensity.intensity),
      Math.round(this.intensity.uncertainty)
    ]
    for (let key of ImpactFactor.keys) {
      if (Object.keys(this.impact.intensity).includes(key)) {
        values.push(this.impact.intensity[key])
      } else {
        values.push(0)
      }
    }
    return values.join(sep)
  }

  isValid () {
    return this.score === 1
  }

  isIncomplete () {
    return false
  }

  isInvalid () {
    return this.score === 2
  }

  setAmount (value) {
    let amount = value
    if (typeof amount === 'string') {
      amount = amount.replace(',', '.').trim().replace(/\s/g, '')
    }
    if (!isNaN(amount)) {
      amount = parseFloat(amount)
    }
    return amount
  }

  getCarbonIntensity (year = null) {
    let ef = EmissionFactor.createFromObj(
      WASTES_FACTORS[this.group][this.type][this.valorization]
    )
    ef = ef.getFactor(year)
    let intensity = new CarbonIntensity(
      this.amount * ef.total.total,
      this.amount * ef.total.total * ef.total.uncertainty,
      ef.group
    )
    this.intensity = intensity
    return intensity
  }

  getImpact (year = null) {
    let ifactors = ImpactFactor.createFromObj(
      WASTES_LCA_FACTORS[this.group][this.type][this.valorization]
    )
    let impact = ImpactIntensity.compute(this.amount, ifactors)
    this.impact = impact
    return impact
  }

  toDatabase () {
    return {
      group: this.group,
      type: this.type,
      valorization: this.valorization,
      amount: this.amount,
      tags: this.tags
    }
  }

  static get formatDescription () {
    return {
      group: {
        pattern: /(group|waste.group|wastegroup|groupe|group.de.dechet)/i,
        required: true,
        converter: function (value) {
          const PATTERNS = {
            packaging: /packaging|emballage/i,
            furniture: /furniture|meubles|ameublement/i,
            'laboratory.waste':
              /laboratory waste|laboratory|d.chets.de.laboratoire|d.chets.laboratoire|laboratoire/i,
            'metals.non.packaging':
              /metals|metals.non.packaging|m.taux|m.taux.hors.emballage/i,
            'health.care.waste':
              /health.care.waste|health.care|d.chets.de.soins|dasri|soins/i,
            'kitchen.waste.and.green.waste':
              /kitchen.waste.and.green.waste|kitchen.waste|green.waste|kitchen|green|d.chets.de.cuisine.et.d.chets.verts|d.chets.de.cuisine|d.chets.verts|cuisine|verts/i,
            'construction.and.demolition.waste':
              /construction.and.demolition.waste|construction|demolition|d.chets.de.construction.et.de.d.molition|construction|d.molition/i,
            'putrescible.waste':
              /putrescible.waste|putrescible|d.chets.putrescibles|putrescibles/i,
            weee: /weee|deee/i,
            'hazardous.industrial.waste':
              /hazardous.industrial.waste|hazardous.industrial|d.chets.industriels.sp.ciaux|industriels.sp.ciaux|DIS/i,
            'residual.household.waste':
              /residual.household.waste|residual.household|household|ordures.m.nag.res.r.siduelles|ordures.m.nag.res/i,
            paper: /paper|papier/i,
            'batteries.and.accumulators':
              /batteries.and.accumulators|batteries|accumulators|piles.et.batteries.en.melange|piles|batteries/i,
            'plastic.non.packaging':
              /plastic.non.packaging|plastic|plastique.hors.emballages|plastique/i,
            'clothing.and.household.textiles':
              /clothing.and.household.textiles|clothing|textiles|habillement.et.textiles.m.nagers|habillement|textiles/i
          }
          let toReturn = ''
          for (let name of Object.keys(PATTERNS)) {
            if (value.search(PATTERNS[name]) >= 0) {
              toReturn = name
            }
          }
          return toReturn
        },
        default: '' // considered as invalid
      },
      type: {
        pattern: /(type|waste.type|wastetype|waste|dechet|type.de.dechet)/i,
        required: true,
        converter: function (value) {
          const PATTERNS = {
            aluminium: /aluminium/i,
            'batteries.and.accumulators':
              /batteries.and.accumulators|batteries|accumulators|piles et batteries en melange|piles|batteries/i,
            cardboard: /cardboard|carton/i,
            'ceramic.and.other.inerts':
              /ceramic.and.other.inerts|ceramic|c.ramiques/i,
            chemical: /chemical|d.chets.chimique/i,
            'clothing.and.household.textiles':
              /clothing.and.household.textiles|clothing|textiles|habillement.et.textiles.m.nagers|habillement/i,
            'concrete.bricks.tiles.and.ceramics':
              /concrete.bricks.tiles.and.ceramics|concrete|bricks|tiles|b.ton..briques..tuiles..c.ramiques|b.ton|briques|tuiles/i,
            copper: /copper|cuivre/i,
            'crt.screens': /crt.screens|ecrans.cathode/i,
            'ferrous.metals': /ferrous.metals|m.taux.ferreux/i,
            'flat.screens': /flat.screens|ecrans.plats/i,
            'flexible.pe': /flexible.pe|plastique.souple.pe/i,
            'flexible.pe.biobased':
              /flexible.pe.biobased|plastique.souple.biosourc..pe/i,
            'flexible.pe.fossilbased':
              /flexible.pe.fossilbased|plastique.souple.p.trosourc..pe/i,
            'flexible.pe.from.agriculture':
              /flexible.pe.from.agriculture|plastique.souple.pe.de.l.agriculture/i,
            'flexible.pet.biobased':
              /flexible.pet.biobased|plastique.souple.biosourc..pet/i,
            'flexible.pet.fossilbased':
              /flexible.pet.fossilbased|plastique.souple.p.trosourc..pet/i,
            'flexible.pp.biobased':
              /flexible.pp.biobased|plastique.souple.biosourc..pp/i,
            'flexible.pp.fossilbased':
              /flexible.pp.fossilbased|plastique.souple.p.trosourc..pp/i,
            'furniture.average': /furniture.average|meubles.par.defaut/i,
            'furniture.bedding':
              /furniture.bedding|bedding|meubles.literie|literie/i,
            'furniture.padded.seating':
              /furniture.padded.seating|padded.seating|meubles.si.ges.rembourr.s|si.ges.rembourr.s/i,
            'furniture.wooden': /furniture.wooden|meubles.en.bois/i,
            glass: /glass|verre/i,
            'green.waste': /green.waste|d.chets.verts/i,
            'hazardous.industrial.waste':
              /hazardous.industrial.waste|hazardous.industrial|d.chets.industriels.sp.ciaux|industriels.sp.ciaux|dis/i,
            'health.care.waste':
              /health.care.waste|health.care|d.chets.d.activit.s.de.soins|activit.s.de.soins|dasri/i,
            infectious: /infectious|d.chets.infectieux|infectieux/i,
            'kitchen.waste': /kitchen.waste|d.chets.de.cuisine/i,
            'kitchen.waste.and.green.waste':
              /kitchen.waste.and.green.waste|d.chets.de.cuisine.et.d.chets.vert/i,
            'lha.cold.at.end.of.life':
              /lha.cold.at.end.of.life|gros..lectrom.nager.froid.en.fin.de.vie|gemf/i,
            'lha.cold.put.on.the.market':
              /lha.cold.put.on.the.market|gros..lectrom.nager.froid.mis.sur.le.march./i,
            'lha.non.cold':
              /lha.non.cold|gros..lectrom.nager.hors.froid|gemhf/i,
            metals: /metals|m.taux/i,
            'mixed.inert.waste':
              /mixed.inert.waste|d.cherts inertes en mélange|gravats|d.cherts inertes/i,
            'non.hazardous.waste.mixed':
              /non.hazardous.waste.mixed|d.chets.non.dangeureux.en.mélange|dib|d.chets.non.dangeureux/i,
            'non.infectious':
              /non.infectious|d.chets.non.infectieux|non.infectieux/i,
            'other.plastics.and.complex.plastics':
              /other.plastics.and.complex.plastics|autres.plastiques.et.plastiques.complexes|autres.plastiques|plastiques.complexes/i,
            paper: /paper|papier/i,
            'pe.biobased': /pe.biobased|plastique.biosourc..pe/i,
            'pe.fossilbased': /pe.fossilbased|plastique.p.trosourc..pe/i,
            'pet.biobased': /pet.biobased|plastique.biosourc..pet/i,
            'pet.fossilbased': /pet.fossilbased|plastique.p.trosourc..pet/i,
            'plaster.and.others.containing.gyspum':
              /plaster.and.others.containing.gyspum|pl.tre.et.autres.contenant.du.gypse|pl.tre|gypse/i,
            'pp.biobased': /pp.biobased|plastique.biosourc..pp/i,
            'pp.fossilbased': /pp.fossilbased|plastique.p.trosourc..pp/i,
            'putrescible.waste': /putrescible.waste|d.chets.putrescibles/i,
            'residual.household.waste':
              /residual.household.waste|household.waste|ordures.m.nag.res.r.siduelles|ordures.m.nag.res/i,
            'rigid.pe': /rigid.pe|plastique.rigide.pe/i,
            'rigid.pe.bottles':
              /rigid.pe.bottles|plastique.rigide.pe.bouteilles/i,
            'rigid.pe.bottles.biobased':
              /rigid.pe.bottles.biobased|plastique.rigide.biosourc..pe.bouteilles/i,
            'rigid.pe.bottles.fossilbased':
              /rigid.pe.bottles.fossilbased|plastique.rigide.p.trosourc..pe.bouteilles/i,
            'rigid.pe.other.packaging':
              /rigid.pe.other.packaging|plastique.rigide.pe.autres.emballages/i,
            'rigid.pe.other.packaging.biobased':
              /rigid.pe.other.packaging.biobased|plastique.rigide.biosourc..pe.autres.emballages/i,
            'rigid.pe.other.packaging.fossilbased':
              /rigid.pe.other.packaging.fossilbased|plastique.rigide.p.trosourc..pe.autres.emballages/i,
            'rigid.pet': /rigid.pet|plastique.rigide.pe/i,
            'rigid.pet.bottles':
              /rigid.pet.bottles|plastique.rigide.pet.bouteilles/i,
            'rigid.pet.bottles.biobased':
              /rigid.pet.bottles.biobased|plastique.rigide.biosourc..pet.bouteilles/i,
            'rigid.pet.bottles.fossilbased':
              /rigid.pet.bottles.fossilbased|plastique.rigide.p.trosourc..pet.bouteilles/i,
            'rigid.pet.other.packaging':
              /rigid.pet.other.packaging|plastique.rigide.pet.autres.emballages/i,
            'rigid.pet.other.packaging.biobased':
              /rigid.pet.other.packaging.biobased|plastique.rigide.biosourc..pet.autres.emballages/i,
            'rigid.pet.other.packaging.fossilbased':
              /rigid.pet.other.packaging.fossilbased|plastique.rigide.p.trosourc..pet.autres.emballages/i,
            'rigid.pp': /rigid.pp|plastique.rigide.pp/i,
            'rigid.pp.bottles':
              /rigid.pp.bottles|plastique.rigide.pp.bouteilles/i,
            'rigid.pp.bottles.biobased':
              /rigid.pp.bottles.biobased|plastique.rigide.biosourc..pp.bouteilles/i,
            'rigid.pp.bottles.fossilbased':
              /rigid.pp.bottles.fossilbased|plastique.rigide.p.trosourc..pp.bouteilles/i,
            'rigid.pp.other.packaging':
              /rigid.pp.other.packaging|plastique.rigide.pp.autres.emballages/i,
            'rigid.pp.other.packaging.biobased':
              /rigid.pp.other.packaging.biobased|plastique.rigide.biosourc..pp.autres.emballages/i,
            'rigid.pp.other.packaging.fossilbased':
              /rigid.pp.other.packaging.fossilbased|plastique.rigide.p.trosourc..pp.autres.emballages/i,
            'rigid.ps': /rigid.ps|plastique.rigide.ps/i,
            'rigid.pseps': /rigid.pseps|plastique.rigide.ps.pse/i,
            'rigid.pvc': /rigid.pvc|plastique.rigide.pvc/i,
            sha: /sha|petits.appareils.en.m.lange|pam|petits.appareils/i,
            steel: /steel|acier/i,
            'tubes.and.lamps':
              /tubes.and.lamps|tubes|lamps|tubes.et.lampes|lampes/i,
            'weee.average.default': /weee.average.default|deee.par.d.faut/i,
            wood: /wood|bois/i,
            'wood.grade.b': /wood.grade.b|bois.de.classe.b/i
          }
          let toReturn = ''
          for (let name of Object.keys(PATTERNS)) {
            if (value.search(PATTERNS[name]) >= 0) {
              toReturn = name
            }
          }
          return toReturn
        },
        default: '' // considered as invalid
      },
      valorization: {
        pattern: /(treatment|treatment.sector|filiere|filiere.de.traitement)/i,
        required: true,
        converter: function (value) {
          const PATTERNS = {
            'average.end.of.life':
              /average.end.of.life|average|fili.re.par.d.faut|d.faut/i,
            'average.end.of.life.without.upstream.losses':
              /average.end.of.life.without.upstream.losses|default.treatment.without.upstream.losses|without.upstream.losses|fili.re.par.d.faut.sans.pertes.en.amont|sans.pertes.en.amont/i,
            'average.end.of.life.with.upstream.losses':
              /average.end.of.life.with.upstream.losses|default.treatment.with.upstream.losses|with.upstream.losses|fili.re.par.d.faut.avec.pertes.en.amont|avec.pertes.en.amont/i,
            composting: /composting|compostage/i,
            'end.of.life.without.recycling':
              /end.of.life.without.recycling|without.recycling|fili.re.hors.recyclage|hors.recyclage/i,
            'home.composting.in.closed.bin':
              /home.composting.in.closed.bin|compostage.domestique.en.bac/i,
            'home.composting.in.heap':
              /home.composting.in.heap|compostage.domestique.en.tas/i,
            incineration: /incineration/i,
            'industrial.composting':
              /industrial.composting|compostage.industriel/i,
            landfill: /landfill|stockage|d.charge/i,
            methanisation: /methanisation|m.thanisation/i,
            recycling: /recycling|recyclage/i,
            'recycling.flakes': /recycling.flakes|flocons.de.recyclage/i,
            'recycling.granulates':
              /recycling.granulates|granulats.de.recyclage/i
          }
          let toReturn = ''
          for (let name of Object.keys(PATTERNS)) {
            if (value.search(PATTERNS[name]) >= 0) {
              toReturn = name
            }
          }
          return toReturn
        },
        default: '' // considered as invalid
      },
      amount: {
        pattern: /(quantite|amount|total|poids|weight)/i,
        converter: floatConverter,
        required: true,
        default: null // considered as invalid
      },
      tags: {
        pattern: /(tags?|labels?)/i,
        required: false,
        converter: tagsConverter,
        default: []
      }
    }
  }

  aggregateDescriptor () {
    return [this.group, this.type, this.valorization, this.tagsHash].join('_')
  }

  _doAggregate (other) {
    let newWaste = Waste.createFromObj(this)
    newWaste.amount += other.amount
    return newWaste
  }

  static get MODULE_NAME () {
    return Modules.WASTES
  }

  static getGroups () {
    let groups = Object.keys(WASTES_FACTORS)
    return Array.from(new Set(groups))
  }

  static getTypes (group) {
    let types = Object.keys(WASTES_FACTORS[group])
    return Array.from(new Set(types))
  }

  static getValorizations (group, type) {
    let valorizations = Object.keys(WASTES_FACTORS[group][type])
    return Array.from(new Set(valorizations))
  }

  static createFromObj (item) {
    return new Waste({
      id: item.id,
      group: item.group,
      type: item.type,
      valorization: item.valorization,
      amount: item.amount,
      tags: item.tags,
      source: item.source
    })
  }

  /**
   * @param {Object[]} lines - The list of wastes object to handle
   * @param {String} tags - The list of tags defined by the user
   * @param {String} source - The source file name
   *
   * @returns {Waste[]} - The list of waste objects
   */
  static createFromLines (lines, format, tags, source) {
    let wastes = []
    for (let line of lines) {
      let nitem = Waste.createFromObj({
        id: null,
        group: line.group,
        type: line.type,
        valorization: line.valorization,
        amount: line.amount,
        source: source
      })
      nitem.addTags(Model.getTagsFromNames(line.tags, tags))
      wastes.push(nitem)
    }
    return wastes
  }

  static exportHeader (sep = '\t') {
    let values = [
      'group',
      'type',
      'valorization',
      'amount',
      'tags',
      'emission.kg.co2e',
      'uncertainty.kg.co2e'
    ]
    for (let key of ImpactFactor.keys) {
      values.push(key)
    }
    return values.join(sep)
  }

  static exportToFile (items, header = true, extraColValue = null, sep = '\t') {
    let headerValues = []
    if (header) {
      headerValues = Waste.exportHeader(sep)
    }
    return [
      headerValues,
      ...items.map(function (item) {
        let val = ''
        if (extraColValue) {
          val += extraColValue + sep
        }
        return val + item.toString(sep)
      })
    ]
      .join('\n')
      .replace(/(^\[)|(\]$)/gm, '')
  }

  static compute (items, year, tags = []) {
    let intensities = new CarbonIntensities()
    let impacts = new ImpactIntensities()
    for (let item of items.filter((obj) => obj.hasTags(tags))) {
      let intensity = item.getCarbonIntensity(year)
      let impact = item.getImpact(year)
      intensities.add(intensity)
      impacts.add(impact)
    }
    return {
      intensity: intensities.sum(),
      impact: impacts.sum()
    }
  }
}
